import React, { Component } from "react";
import { observer } from "mobx-react";
import qs from "qs";
import { getRequest } from "../../utils/httpRequest";
import PricingStore from "../../stores/PricingStore";
import {
  Landing,
  Instructors,
  AiCoursePicker,
  FeaturedCourses,
  CommunityHub,
  Introduction,
  Reviews,
  NewPricing,
  Subscription,
  SampleCourses,
  Blog,
  WhySection,
  EasterEggs,
  Cast,
} from "./components";
import { RootContext } from "../../stores/RootStore/Types";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";

interface Props {
  handleShowSignInSideBar: () => void;
  pricingStore: PricingStore;
  location: {
    search: string;
  };
  currentUser: CurrentUserResource;
}

interface State {
  courses: any[];
  enrolmentCount: number;
  courseCount: number;
}

const CACHE_KEY = "landing_courses_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

class Home extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      courses: [],
      enrolmentCount: 0,
      courseCount: 0,
    };
  }

  componentDidMount() {
    this.loadData();

    const { search } = this.props.location;
    const queryParams = qs.parse(search, { ignoreQueryPrefix: true });

    if (queryParams?.scroll_to) {
      setTimeout(() => {
        const elem = document.getElementById(`${queryParams?.scroll_to}`);
        const headerOffset = 90;
        const elementPosition = elem.getBoundingClientRect().top;
        let offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }, 3000);
    }
  }

  loadData = async () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);

      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const isExpired = Date.now() - parsed.timestamp > CACHE_DURATION;

          if (!isExpired) {
            this.setState({
              courses: parsed.data.courses,
              enrolmentCount: parsed.data.enrolment_count,
              courseCount: parsed.data.course_count,
            });
            return; // skip API call
          }
        } catch (err) {
          console.warn("Invalid cache, clearing...");
          localStorage.removeItem(CACHE_KEY);
        }
      }

      // Fetch fresh data
      const response: any = await getRequest("/api/landing_courses.json");
      const { data } = response;

      this.setState({
        courses: data.courses,
        courseCount: data.course_count,
        enrolmentCount: data.enrolment_count,
      });

      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data,
            timestamp: Date.now(),
          }),
        );
      } catch (err) {
        console.warn("Failed to write cache:", err);
      }
    } catch (err) {
      console.error("API request failed:", err);
    }
  };

  renderPricing = (state) => {
    switch (state.kind) {
      case "ready":
        return (
          <NewPricing
            handleShowSignInSideBar={this.props.handleShowSignInSideBar}
            pricing={state.pricing}
            showSigninSidebar={true}
            singleCourseHeading="Lifetime single <br/> course access"
          />
        );
      case "loading":
        return (
          <div
            className="pricing ui vertical center aligned segment"
            id="pricing-container"
          >
            <div className="ui active inverted dimmer">
              <div className="ui text loader">Loading</div>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  };

  render() {
    const { pricingState } = this.props.pricingStore;
    const { currentUser } = this.props;
    const { courses, enrolmentCount, courseCount } = this.state;

    return (
      <RootContext.Consumer>
        {() => {
          return (
            <div>
              <a href="#main" className="sr-only skip-link">
                Skip to main content
              </a>
              <div className="new-landing-page">
                <div className="ui facelift-landing-page" id="main">
                  <Landing
                    userIsInUk={currentUser.is_in_uk}
                    courses={courses}
                    enrolmentCount={enrolmentCount}
                    courseCount={courseCount}
                  />
                  <Instructors />
                  <FeaturedCourses userIsInUk={currentUser.is_in_uk} />
                  <AiCoursePicker userIsInUk={currentUser.is_in_uk} />
                  <Reviews />
                  <SampleCourses userIsInUk={currentUser.is_in_uk} />
                  <Introduction />
                  <CommunityHub
                    handleShowSignInSideBar={this.props.handleShowSignInSideBar}
                  />
                  <WhySection />
                  {this.renderPricing(pricingState)}
                  <Cast />
                  <Blog />
                  <Subscription />
                </div>
              </div>
              <EasterEggs />
            </div>
          );
        }}
      </RootContext.Consumer>
    );
  }
}

export default observer(Home);
